/** Geographic and projected coordinate primitives shared by every world stage. */

export const WEB_MERCATOR_MAX_LATITUDE = 85.0511287798066;
export const EARTH_RADIUS_METERS = 6_378_137;
export const EARTH_CIRCUMFERENCE_METERS = 2 * Math.PI * EARTH_RADIUS_METERS;

/** WGS84 longitude/latitude in degrees. */
export interface GeoPoint {
  readonly lon: number;
  readonly lat: number;
}

/** WGS84 bounds. `west > east` denotes an antimeridian-crossing extent. */
export interface GeoBounds {
  readonly west: number;
  readonly south: number;
  readonly east: number;
  readonly north: number;
}

/**
 * Spherical Web Mercator metres with X east and Z south.
 *
 * The origin is (0, 0) at lon/lat (0, 0). Z is the negated EPSG:3857 Y axis,
 * matching the X-east/Y-up/Z-south convention used by compiled geometry.
 */
export interface MercatorPoint {
  readonly x: number;
  readonly z: number;
}

export interface LocalPoint2 {
  readonly x: number;
  readonly z: number;
}

export interface LocalPoint3 extends LocalPoint2 {
  readonly y: number;
}

export interface LocalBounds2 {
  readonly minX: number;
  readonly minZ: number;
  readonly maxX: number;
  readonly maxZ: number;
}

/**
 * A local metric tangent frame used while solving one macrotile.
 *
 * Web Mercator delta values are multiplied by `mercatorToLocalScale`, which is
 * cos(anchor latitude), to recover ground-scale metres around the anchor.
 */
export interface LocalMetricFrame {
  readonly anchor: GeoPoint;
  readonly anchorMercator: MercatorPoint;
  readonly mercatorToLocalScale: number;
  readonly axes: "x-east-y-up-z-south";
}

export function clampMercatorLatitude(latitude: number): number {
  assertFiniteNumber(latitude, "latitude");
  return Math.max(
    -WEB_MERCATOR_MAX_LATITUDE,
    Math.min(WEB_MERCATOR_MAX_LATITUDE, latitude),
  );
}

/** Normalises a longitude into [-180, 180). */
export function wrapLongitude(longitude: number): number {
  assertFiniteNumber(longitude, "longitude");
  const wrapped = ((longitude + 180) % 360 + 360) % 360 - 180;
  return Object.is(wrapped, -0) ? 0 : wrapped;
}

export function normaliseGeoPoint(point: GeoPoint): GeoPoint {
  return {
    lon: wrapLongitude(point.lon),
    lat: clampMercatorLatitude(point.lat),
  };
}

/** Eastward longitude span in (0, 360], or zero for a degenerate extent. */
export function longitudeSpanDegrees(bounds: Pick<GeoBounds, "west" | "east">): number {
  assertFiniteNumber(bounds.west, "west longitude");
  assertFiniteNumber(bounds.east, "east longitude");
  const direct = bounds.east - bounds.west;
  if (direct > 0) return Math.min(360, direct);
  if (direct < 0) return direct + 360;
  return 0;
}

/** Eastward offset from `west`, suitable for wrapped geographic grids. */
export function longitudeOffsetDegrees(longitude: number, west: number): number {
  assertFiniteNumber(longitude, "longitude");
  assertFiniteNumber(west, "west longitude");
  return ((longitude - west) % 360 + 360) % 360;
}

export function projectGeoPoint(point: GeoPoint): MercatorPoint {
  const normalised = normaliseGeoPoint(point);
  const longitudeRadians = degreesToRadians(normalised.lon);
  const latitudeRadians = degreesToRadians(normalised.lat);

  return {
    x: EARTH_RADIUS_METERS * longitudeRadians,
    z: -EARTH_RADIUS_METERS * Math.asinh(Math.tan(latitudeRadians)),
  };
}

export function unprojectMercator(point: MercatorPoint): GeoPoint {
  assertFiniteNumber(point.x, "Mercator X");
  assertFiniteNumber(point.z, "Mercator Z");

  return {
    lon: wrapLongitude(radiansToDegrees(point.x / EARTH_RADIUS_METERS)),
    lat: clampMercatorLatitude(
      radiansToDegrees(Math.atan(Math.sinh(-point.z / EARTH_RADIUS_METERS))),
    ),
  };
}

export function createLocalMetricFrame(anchor: GeoPoint): LocalMetricFrame {
  const normalisedAnchor = normaliseGeoPoint(anchor);
  return {
    anchor: normalisedAnchor,
    anchorMercator: projectGeoPoint(normalisedAnchor),
    mercatorToLocalScale: Math.cos(degreesToRadians(normalisedAnchor.lat)),
    axes: "x-east-y-up-z-south",
  };
}

export function projectGeoPointToFrame(
  point: GeoPoint,
  frame: LocalMetricFrame,
): LocalPoint2 {
  const projected = projectGeoPoint(point);
  const wrappedDeltaX = shortestWrappedMercatorDelta(
    projected.x - frame.anchorMercator.x,
  );

  return {
    x: wrappedDeltaX * frame.mercatorToLocalScale,
    z: (projected.z - frame.anchorMercator.z) * frame.mercatorToLocalScale,
  };
}

export function unprojectFramePoint(
  point: LocalPoint2,
  frame: LocalMetricFrame,
): GeoPoint {
  assertFiniteNumber(point.x, "local X");
  assertFiniteNumber(point.z, "local Z");
  if (!(frame.mercatorToLocalScale > 0)) {
    throw new RangeError("Local metric frame scale must be greater than zero");
  }

  return unprojectMercator({
    x:
      frame.anchorMercator.x +
      point.x / frame.mercatorToLocalScale,
    z:
      frame.anchorMercator.z +
      point.z / frame.mercatorToLocalScale,
  });
}

export function assertFiniteNumber(value: number, label: string): void {
  if (!Number.isFinite(value)) {
    throw new TypeError(`${label} must be a finite number`);
  }
}

function shortestWrappedMercatorDelta(delta: number): number {
  if (delta > EARTH_CIRCUMFERENCE_METERS / 2) {
    return delta - EARTH_CIRCUMFERENCE_METERS;
  }
  if (delta < -EARTH_CIRCUMFERENCE_METERS / 2) {
    return delta + EARTH_CIRCUMFERENCE_METERS;
  }
  return delta;
}

function degreesToRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

function radiansToDegrees(radians: number): number {
  return (radians * 180) / Math.PI;
}
